import React, { useEffect, useMemo, useRef, useState } from "react";
import { CancelManager } from "../lib/cancelManager.ts";
import {
  Autocomplete,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import {
  ExportProgressModal,
  ExportProgressStatus,
} from "./parts/ExportProgressModal.tsx";
import { zendeskHttpRequest } from "../lib/api/zendeskHttpRequest.ts";
import { exportCustomerData } from "../usecases/exportCustomerData.ts";
import { config, CsvEncoding } from "../lib/config.ts";
import { CsvEncodingSelect } from "./parts/CsvEncodingSelect.tsx";

export const CustomerAppContainer: React.FC<{ token: string }> = ({
  token,
}) => {
  const [visibleModal, setVisibleModal] = useState<boolean>(false);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [downloadedCount, setDownloadedCount] = useState<number>(0);
  const [fetchedCount, setFetchedCount] = useState<number>(0);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [exportProgressInfo, setExportProgressInfo] = useState<{
    status: ExportProgressStatus;
    message: string;
  }>({ status: "Export", message: "" });
  const exportGenRef = useRef<number>(0);

  const [role, setRole] = useState<string>("end_user");
  const [excludeInternal, setExcludeInternal] = useState<boolean>(
    config.enableInternalOrgExclusion,
  );
  const [excludeSuspended, setExcludeSuspended] = useState<boolean>(true);

  const [createdStartDate, setCreatedStartDate] = useState<string>("");
  const [createdEndDate, setCreatedEndDate] = useState<string>("");
  const [updatedStartDate, setUpdatedStartDate] = useState<string>("");
  const [updatedEndDate, setUpdatedEndDate] = useState<string>("");

  const [selectedOrgs, setSelectedOrgs] = useState<
    { id: number; name: string }[]
  >([]);
  const [nameKeyword, setNameKeyword] = useState<string>("");
  const [selectedIndustries, setSelectedIndustries] = useState<string[]>([]);
  const industryOptions = useMemo(() => {
    const raw = import.meta.env.VITE_INDUSTRY_OPTIONS ?? "";
    return raw
      .split(",")
      .map((s: string) => s.trim())
      .filter((s: string) => s !== "");
  }, []);
  const [addressKeyword, setAddressKeyword] = useState<string>("");
  const [departmentKeyword, setDepartmentKeyword] = useState<string>("");

  const [allOrgs, setAllOrgs] = useState<{ id: number; name: string }[]>([]);
  const [orgLoading, setOrgLoading] = useState<boolean>(true);

  const [csvEncoding, setCsvEncoding] = useState<CsvEncoding>(
    config.defaultCsvEncoding,
  );

  // コンポーネント起動時に全組織を取得
  useEffect(() => {
    const loadOrgs = async () => {
      setOrgLoading(true);
      try {
        const baseUrl = `https://${import.meta.env.VITE_ZENDESK_SUBDOMAIN}.zendesk.com`;
        const collected: { id: number; name: string }[] = [];
        let nextPage: string | null =
          `${baseUrl}/api/v2/organizations.json?page[size]=100`;

        while (nextPage) {
          const res = (await zendeskHttpRequest(nextPage, token, "GET")) as {
            organizations: { id: number; name: string }[];
            meta: { has_more: boolean };
            links: { next: string | null };
          };
          collected.push(...res.organizations);
          nextPage = res.meta.has_more ? res.links.next : null;
        }

        setAllOrgs(collected);
      } catch {
        setAllOrgs([]);
      } finally {
        setOrgLoading(false);
      }
    };
    loadOrgs();
  }, [token]);

  const handleDateChange =
    (setter: React.Dispatch<React.SetStateAction<string>>) =>
    (newValue: Dayjs | null) => {
      setter(newValue ? newValue.format("YYYY-MM-DD") : "");
    };

  const handleExport = async () => {
    if (isExporting) return;
    const gen = ++exportGenRef.current;
    setIsExporting(true);
    setTotalCount(0);
    setDownloadedCount(0);
    setFetchedCount(0);
    setExportProgressInfo({ status: "Export", message: "" });
    setVisibleModal(true);
    await exportCustomerData({
      token,
      role,
      excludeInternal,
      excludeSuspended,
      createdStartDate,
      createdEndDate,
      updatedStartDate,
      updatedEndDate,
      selectedOrgs,
      allOrgs,
      nameKeyword,
      selectedIndustries,
      addressKeyword,
      departmentKeyword,
      onUpdateTotalCount: (count) => {
        if (exportGenRef.current === gen) setTotalCount(count);
      },
      onUpdateDownloadedCount: (count) => {
        if (exportGenRef.current === gen) setDownloadedCount(count);
      },
      onUpdateFetchedCount: (count) => {
        if (exportGenRef.current === gen) setFetchedCount(count);
      },
      onUpdateInfo: (status, message) => {
        if (exportGenRef.current === gen)
          setExportProgressInfo({ status, message });
      },
      csvEncoding,
    });
    if (exportGenRef.current === gen) setIsExporting(false);
  };

  return (
    <Container maxWidth="md">
      <Grid container spacing={2} sx={{ mt: 5 }} alignItems="center">
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel>ロール</InputLabel>
            <Select
              value={role}
              label="ロール"
              onChange={(e) => setRole(e.target.value)}
            >
              <MenuItem value="end_user">エンドユーザーのみ</MenuItem>
              <MenuItem value="agent">エージェント・管理者のみ</MenuItem>
              <MenuItem value="all">すべて</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mt: 1 }} alignItems="center">
        {config.enableInternalOrgExclusion && (
          <Grid item xs={6}>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={excludeInternal}
                    sx={{
                      color: "#00897B",
                      "&.Mui-checked": { color: "#00897B" },
                    }}
                    onChange={(e) => setExcludeInternal(e.target.checked)}
                  />
                }
                label="社内ユーザー除外"
              />
            </FormGroup>
          </Grid>
        )}
        <Grid item xs={6}>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={excludeSuspended}
                  sx={{
                    color: "#00897B",
                    "&.Mui-checked": { color: "#00897B" },
                  }}
                  onChange={(e) => setExcludeSuspended(e.target.checked)}
                />
              }
              label="一時停止ユーザー除外"
            />
          </FormGroup>
        </Grid>
      </Grid>

      {/* 組織フィルタ（読み込み完了後に操作可能） */}
      <Grid container spacing={2} sx={{ mt: 3 }}>
        <Grid item xs={12}>
          <Autocomplete
            multiple
            disabled={orgLoading}
            options={allOrgs}
            loading={orgLoading}
            value={selectedOrgs}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(_event, value) => setSelectedOrgs(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                label={
                  orgLoading
                    ? "組織データを読み込み中..."
                    : "組織（候補から選択）"
                }
                variant="outlined"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {orgLoading ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Grid>
      </Grid>

      {/* 顧客名キーワード */}
      <Grid container spacing={2} sx={{ mt: 3 }}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="顧客名キーワード"
            variant="outlined"
            value={nameKeyword}
            onChange={(e) => setNameKeyword(e.target.value)}
          />
        </Grid>
      </Grid>

      {/* 作成日・更新日（横並び） */}
      <Grid container spacing={2} sx={{ mt: 3 }}>
        <Grid item xs={6}>
          <Typography variant="body2" textAlign="left" sx={{ mb: 1 }}>
            作成日（期間指定）
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="開始日"
                  format="YYYY/MM/DD"
                  slotProps={{ calendarHeader: { format: "YYYY年MM月" } }}
                  value={createdStartDate ? dayjs(createdStartDate) : null}
                  onChange={handleDateChange(setCreatedStartDate)}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="終了日"
                  format="YYYY/MM/DD"
                  slotProps={{ calendarHeader: { format: "YYYY年MM月" } }}
                  value={createdEndDate ? dayjs(createdEndDate) : null}
                  onChange={handleDateChange(setCreatedEndDate)}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={6}>
          <Typography variant="body2" textAlign="left" sx={{ mb: 1 }}>
            更新日（期間指定）
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="開始日"
                  format="YYYY/MM/DD"
                  slotProps={{ calendarHeader: { format: "YYYY年MM月" } }}
                  value={updatedStartDate ? dayjs(updatedStartDate) : null}
                  onChange={handleDateChange(setUpdatedStartDate)}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="終了日"
                  format="YYYY/MM/DD"
                  slotProps={{ calendarHeader: { format: "YYYY年MM月" } }}
                  value={updatedEndDate ? dayjs(updatedEndDate) : null}
                  onChange={handleDateChange(setUpdatedEndDate)}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {config.enableIndustryFilter && (
        <>
          <Grid container spacing={2} sx={{ mt: 3 }}>
            <Grid item xs={6}>
              <Autocomplete
                multiple
                options={industryOptions}
                value={selectedIndustries}
                onChange={(_event, value) => setSelectedIndustries(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    label="業種（候補から選択）"
                    variant="outlined"
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="住所キーワード"
                variant="outlined"
                value={addressKeyword}
                onChange={(e) => setAddressKeyword(e.target.value)}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mt: 3 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="部署名キーワード"
                variant="outlined"
                value={departmentKeyword}
                onChange={(e) => setDepartmentKeyword(e.target.value)}
              />
            </Grid>
          </Grid>
        </>
      )}

      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item xs={12}>
          <CsvEncodingSelect value={csvEncoding} onChange={setCsvEncoding} />
        </Grid>
      </Grid>

      <Grid>
        <Button
          variant="contained"
          style={{ marginTop: "16px" }}
          onClick={handleExport}
          disabled={isExporting}
          sx={{
            backgroundColor: "#00897B",
            "&:hover": { backgroundColor: "#00695C" },
          }}
        >
          エクスポート実行
        </Button>
        <ExportProgressModal
          open={visibleModal}
          onClose={() => {
            CancelManager.getInstance().cancel();
            setIsExporting(false);
            setVisibleModal(false);
            setTotalCount(0);
            setDownloadedCount(0);
            setFetchedCount(0);
            setExportProgressInfo({ status: "Export", message: "" });
          }}
          totalCount={totalCount}
          downloadedCount={downloadedCount}
          fetchedCount={fetchedCount}
          progressInfo={exportProgressInfo}
        />
      </Grid>
    </Container>
  );
};
